// Shared monetary value parser used by inventory import and manual price entry.
// Centralises the logic that used to live as a private `cleanMonetaryValue` method
// duplicated in inventory-dashboard.component.ts.

export interface ParsedMonetaryValue {
  /** Parsed numeric value, or null if parsing failed. */
  value: number | null;
  /** Human-readable error, or null if parsing succeeded (with or without correction). */
  error: string | null;
  /** True when the input needed correction (currency symbols/thousands separators stripped, etc.) to parse. */
  sanitised: boolean;
  /** The original input, stringified, for display in "needs review" reporting. */
  rawValue: string;
}

// Unicode dash/minus variants that are NOT the ASCII hyphen-minus. These are commonly
// pasted from Excel/Word ("–" en dash, "—" em dash) in place of a real minus sign and
// silently fail Number() parsing (falling through to `|| 0` in older callers).
const UNICODE_DASHES = /[‐‑‒–—―−]/;

// Ordered longest-first so multi-letter ISO codes (e.g. "ZAR") are stripped before the
// single-letter symbols they contain (e.g. "R"), which would otherwise leave a stray "ZA".
const CURRENCY_SYMBOLS = ['ZAR', 'USD', 'EUR', 'GBP', 'JPY', 'INR', 'R', '$', '€', '£', '¥', '₹'];

// Disallow numeric literals that JS `Number()` accepts but that are never valid prices:
// hex ("0x1F"), exponent notation ("1e10"), and Infinity.
const DISALLOWED_LITERAL = /^\s*[+-]?(0x[0-9a-f]+|infinity|[0-9.]+e[+-]?\d+)\s*$/i;

/**
 * Extracts a plain string/number from an ExcelJS cell value, which can be a primitive,
 * a formula result (`{ result }`), a hyperlink cell (`{ text }`), or rich text
 * (`{ richText: [{ text }, ...] }`). Previously these object shapes stringified to
 * "[object Object]" and broke downstream parsing.
 */
function extractExcelCellValue(input: any): any {
  if (input === null || input === undefined) {
    return input;
  }
  if (typeof input === 'object') {
    if (Array.isArray(input.richText)) {
      return input.richText.map((part: any) => part?.text ?? '').join('');
    }
    if (input.result !== undefined) {
      return input.result;
    }
    if (input.text !== undefined) {
      return input.text;
    }
  }
  return input;
}

export function parseMonetary(input: any): ParsedMonetaryValue {
  const extracted = extractExcelCellValue(input);
  const rawValue = extracted === null || extracted === undefined ? '' : String(extracted);

  if (extracted === null || extracted === undefined || extracted === '') {
    return { value: null, error: null, sanitised: false, rawValue };
  }

  const stringValue = String(extracted).trim();

  if (UNICODE_DASHES.test(stringValue)) {
    return {
      value: null,
      error: `Invalid price format: "${stringValue}" contains a dash character that is not a valid minus sign. Please enter a plain numeric value.`,
      sanitised: false,
      rawValue
    };
  }

  if (DISALLOWED_LITERAL.test(stringValue)) {
    return {
      value: null,
      error: `Invalid price format: "${stringValue}". Please use plain numeric values only (e.g., 10.50).`,
      sanitised: false,
      rawValue
    };
  }

  // Already a valid plain number.
  if (stringValue !== '' && !isNaN(Number(stringValue))) {
    const numValue = Number(stringValue);
    if (numValue < 0) {
      return { value: null, error: 'Price cannot be negative', sanitised: false, rawValue };
    }
    return { value: numValue, error: null, sanitised: false, rawValue };
  }

  // Needs correction: strip currency symbols/whitespace/thousands separators.
  let cleanedValue = stringValue;

  CURRENCY_SYMBOLS.forEach(symbol => {
    const regex = new RegExp(symbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    cleanedValue = cleanedValue.replace(regex, '');
  });

  cleanedValue = cleanedValue.trim();

  // Comma as decimal separator (e.g. "1234,56" -> "1234.56").
  const commaMatch = cleanedValue.match(/^(\d+),(\d{1,2})$/);
  if (commaMatch) {
    cleanedValue = `${commaMatch[1]}.${commaMatch[2]}`;
  }

  // Thousands separators: spaces ("1 234.56") or commas ("1,234.56").
  cleanedValue = cleanedValue.replace(/(\d)\s+(\d)/g, '$1$2');
  cleanedValue = cleanedValue.replace(/(\d),(\d{3})/g, '$1$2');

  if (cleanedValue === '' || isNaN(Number(cleanedValue)) || DISALLOWED_LITERAL.test(cleanedValue)) {
    return {
      value: null,
      error: `Invalid price format: "${stringValue}". Please use numeric values only (e.g., 10.50)`,
      sanitised: false,
      rawValue
    };
  }

  const numValue = Number(cleanedValue);
  if (numValue < 0) {
    return { value: null, error: 'Price cannot be negative', sanitised: false, rawValue };
  }

  return { value: numValue, error: null, sanitised: true, rawValue };
}
